import type { GoogleLoginProps } from "@react-oauth/google";
import { useGoogleOAuth } from "@react-oauth/google";
import { Box, Center } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useThemePreference } from "theme/ThemePreferenceProvider";

// Uses `VITE_API_HOSTNAME` for local development, otherwise pull from the domain like `client.ts`.
const url = import.meta.env.VITE_API_HOSTNAME || window.location.origin;
const tokenEndpoint = `${url}/api/idp/provider/ad3e5d03-22ba-4588-828b-2926fa88d60b/oidc/token`;

// Shared with the marketing website (timescale-website), which sets these on
// this domain before starting its own Google SSO flow — see IDPLanding, which
// reads and clears them once the OAuth round trip completes. Only valid in
// prod, where both apps actually share this parent domain. Values written
// there are `encodeURIComponent`-escaped; js-cookie's default read converter
// decodes them back automatically. Domain lives in utils/attribution
// alongside the Varify/Gravity handoff cookies that share it.
export const SUBMISSION_PAGE_COOKIE_NAME = "submission_page";
export const UTM_FORM_PAGE_HANDOFF_COOKIE_NAME = "utm_form_page_handoff";

// `login_uri` doubles as the OAuth `redirect_uri`, which Google validates
// against the registered Authorized redirect URIs — query string included. A
// per-invite hash can never match a registered value, so we can't smuggle it
// there (doing so triggers `redirect_uri_mismatch`). Instead we drop it in a
// first-party cookie that rides along on Google's cross-site POST back to the
// token endpoint, where the backend reads it. The cross-site POST means the
// cookie must be `SameSite=None; Secure` to be sent.
const INVITE_HASH_COOKIE_MAX_AGE_SECONDS = 10 * 60;
const setInviteHashCookie = (inviteHash: string) => {
  document.cookie = `inviteHash=${encodeURIComponent(
    inviteHash,
  )}; path=/; max-age=${INVITE_HASH_COOKIE_MAX_AGE_SECONDS}; SameSite=None; Secure`;
};

// The backend has to prove this cross-site POST was started from our own page.
// Google's `g_csrf_token` cookie can't carry that: GSI marks it
// `SameSite=None` only for Chromium user agents, so outside Chromium it is
// never sent and the check can never pass. We bind the callback with a value of
// our own instead — Google copies `nonce` into the credential's `nonce` claim,
// which the backend compares against this cookie.
//
// The window is wider than the invite hash's because an expired cookie fails
// the login outright, and the binding rests on the value being unguessable and
// unreadable cross-origin rather than on a short lifetime.
//
// The `__Host-` prefix is load-bearing, not decoration: it forbids a `Domain`
// attribute, so no sibling `*.tigerdata.com` host can plant a nonce of its own
// choosing on this one. Without it an attacker who controls any subdomain can
// set the cookie, then ask Google for a credential carrying that same nonce,
// and the comparison passes. The prefix also requires `Secure` and `path=/`,
// both of which this cookie already sets — a browser silently drops the cookie
// if any of the three stops holding.
export const LOGIN_NONCE_COOKIE_NAME = "__Host-googleLoginNonce";
const LOGIN_NONCE_COOKIE_MAX_AGE_SECONDS = 30 * 60;

// One value per page load, handed to Google and written to the cookie together
// so the claim and the cookie cannot disagree.
//
// Generated on first use rather than at module load: `crypto.randomUUID` is
// only defined in a secure context, so an eager call throws during import on a
// plain-http origin and takes every page that imports this one down with it.
//
// It also confines the value to `[0-9a-f-]`, which is what allows it into the
// cookie below unencoded. Percent-encoding it here would desync it from the
// `nonce` claim, because the backend compares the cookie without unescaping.
let loginNonce: string | undefined;
const getLoginNonce = () => (loginNonce ??= crypto.randomUUID());

const setLoginNonceCookie = () => {
  document.cookie = `${LOGIN_NONCE_COOKIE_NAME}=${getLoginNonce()}; path=/; max-age=${LOGIN_NONCE_COOKIE_MAX_AGE_SECONDS}; SameSite=None; Secure`;
};

// Google renders the button as a fixed-width iframe rather than filling its
// container, so it has to be told how much room it has. These are the bounds
// its API accepts; below 200 Google clips the label.
// https://developers.google.com/identity/gsi/web/reference/js-reference#width
const MIN_BUTTON_WIDTH = 200;
const MAX_BUTTON_WIDTH = 400;

// Dragging a window changes the width the button has room for on every frame,
// and each re-render rebuilds the button Google injected. Wait for the width to
// settle instead. The first measurement is not delayed: it decides the width
// the button is first painted at.
const SETTLE_MS = 150;

// `google.accounts.id.initialize` is a global singleton — calling it on every
// mount warns and clobbers the previous config. Dedupe on the resolved config
// so re-mounts (StrictMode dev, navigating between auth pages) skip re-init,
// while a changed config still re-initializes.
const initializeGoogleSSO = (() => {
  let lastConfig: string | null = null;
  return (clientId: string, loginUri: string) => {
    const config = `${clientId}|${loginUri}|${getLoginNonce()}`;
    if (config === lastConfig) return;
    lastConfig = config;
    window.google.accounts.id.initialize({
      client_id: clientId,
      login_uri: loginUri,
      ux_mode: "redirect",
      nonce: getLoginNonce(),
    });
  };
})();

export const GoogleSSOButton = ({
  text,
  inviteHash,
}: Pick<GoogleLoginProps, "text"> & { inviteHash?: string | null }) => {
  const { clientId, scriptLoadedSuccessfully } = useGoogleOAuth();
  const { effectiveColorMode } = useThemePreference();
  const containerRef = useRef<HTMLDivElement>(null);
  const [buttonWidth, setButtonWidth] = useState<number>();

  // The button is sized to the room the card gives it. Left at Google's maximum
  // it overflows a narrow card, and on a card whose width follows its contents
  // it widens the whole page the moment it paints.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measuredWidth = () =>
      Math.min(
        MAX_BUTTON_WIDTH,
        Math.max(MIN_BUTTON_WIDTH, Math.floor(container.clientWidth)),
      );

    setButtonWidth(measuredWidth());

    let settleTimer = 0;
    const observer = new ResizeObserver(() => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(
        () => setButtonWidth(measuredWidth()),
        SETTLE_MS,
      );
    });
    observer.observe(container);

    return () => {
      window.clearTimeout(settleTimer);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!scriptLoadedSuccessfully || !containerRef.current || !buttonWidth) {
      return;
    }

    if (inviteHash) setInviteHashCookie(inviteHash);
    // Rewritten on every run, not just the first: `initializeGoogleSSO` is
    // deduped, so this is what keeps the cookie alive alongside a config
    // Google already has.
    setLoginNonceCookie();
    initializeGoogleSSO(clientId, tokenEndpoint);

    window.google.accounts.id.renderButton(containerRef.current, {
      type: "standard",
      shape: "square",
      theme: effectiveColorMode === "dark" ? "filled_black" : "outline",
      size: "large",
      text,
      logo_alignment: "left",
      width: buttonWidth,
    });
  }, [
    clientId,
    scriptLoadedSuccessfully,
    effectiveColorMode,
    text,
    inviteHash,
    buttonWidth,
  ]);

  return (
    <Center
      // The wrapper takes the width its parent gives it, never the width of the
      // button it holds. Left to size itself it shrink-wraps to an empty box
      // before Google paints, so an `align-items: center` parent measures 0 and
      // the button pins itself to Google's minimum for good. The cap keeps the
      // dark-mode border hugging the button once the parent is wider than it.
      w="100%"
      maxW={`${MAX_BUTTON_WIDTH}px`}
      sx={{ colorScheme: "light" }}
      _dark={{
        border: "1px solid",
        borderColor: "neutral.emphasized",
        borderRadius: "sm",
        overflow: "hidden",
      }}
    >
      <Box ref={containerRef} w="100%" h="10" />
    </Center>
  );
};
